import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Check, ChevronDown, Loader2, MailCheck, CalendarClock, PhoneCall } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const INDUSTRIES = [
  "Construction & Trades",
  "Professional Services",
  "Retail & E-commerce",
  "Hospitality",
  "Healthcare",
  "Manufacturing",
  "Tech & SaaS",
  "Transport & Logistics",
  "Agriculture",
  "Other",
];

const TENURES = ["Less than 1 year", "1–2 years", "3–5 years", "6–10 years", "More than 10 years"];

const CHALLENGES = [
  "Customers taking too long to pay",
  "Not enough cash left after expenses",
  "Managing BAS, tax or other large payments",
  "Understanding where the money is going",
  "Planning ahead / forecasting cash flow",
  "Growing the business without putting pressure on cash",
  "Seasonal or inconsistent income",
  "Other",
];

const nextSteps = [
  { icon: MailCheck, text: "We confirm your registration by email." },
  { icon: CalendarClock, text: "You get your webinar link and calendar invite before 12 November." },
  { icon: PhoneCall, text: "If you would like, we can have a quick chat about your business and any cash flow challenges you are facing." },
];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-[#0FA3E8] focus:ring-4 focus:ring-[#0FA3E8]/15";

function Field({ label, required, error, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">
        {label} {required && <span className="text-[#0FA3E8]">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1.5 text-xs font-medium text-red-500" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

function RadioCard({ name, value, label, selected, onSelect, testid }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(value)}
      className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition-all duration-200 active:scale-[0.99] ${
        selected
          ? "border-[#0FA3E8] bg-[#F0F9FF] text-[#0B2B4B] shadow-sm"
          : "border-slate-200 bg-white text-slate-600 hover:border-[#0FA3E8]/50 hover:bg-slate-50"
      }`}
      data-testid={testid}
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
          selected ? "border-[#0FA3E8]" : "border-slate-300"
        }`}
      >
        {selected && <span className="h-2 w-2 rounded-full bg-[#0FA3E8]" />}
      </span>
      {label}
    </button>
  );
}

function CheckCard({ label, selected, onToggle, testid }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={onToggle}
      className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition-all duration-200 active:scale-[0.99] ${
        selected
          ? "border-[#0FA3E8] bg-[#F0F9FF] text-[#0B2B4B] shadow-sm"
          : "border-slate-200 bg-white text-slate-600 hover:border-[#0FA3E8]/50 hover:bg-slate-50"
      }`}
      data-testid={testid}
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-md border-2 transition-colors duration-200 ${
          selected ? "border-[#0FA3E8] bg-[#0FA3E8]" : "border-slate-300"
        }`}
      >
        {selected && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
      </span>
      {label}
    </button>
  );
}

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  business_name: "",
  industry: "",
  tenure: "",
  biggest_challenge: [],
  webinar_goal: "",
  contact_consent: "",
};

export default function EoiForm() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState(null);

  const set = (key) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const toggleChallenge = (value) => {
    setForm((f) => ({
      ...f,
      biggest_challenge: f.biggest_challenge.includes(value)
        ? f.biggest_challenge.filter((v) => v !== value)
        : [...f.biggest_challenge, value],
    }));
    setErrors((er) => ({ ...er, biggest_challenge: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.first_name.trim()) er.first_name = "First name is required";
    if (!form.last_name.trim()) er.last_name = "Last name is required";
    if (!form.email.trim()) er.email = "Email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Enter a valid email address";
    if (!form.business_name.trim()) er.business_name = "Business name is required";
    if (!form.industry) er.industry = "Please select your industry";
    if (!form.tenure) er.tenure = "Please select one";
    if (form.biggest_challenge.length === 0) er.biggest_challenge = "Please select at least one";
    if (!form.contact_consent) er.contact_consent = "Please select one";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/registrations`, {
        ...form,
        mobile: form.mobile.trim() || null,
        webinar_goal: form.webinar_goal.trim() || null,
      });
      setSuccessId(data.id);
      window.scrollTo({ top: document.getElementById("register").offsetTop - 60, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="register" className="relative overflow-hidden py-20 md:py-28 grain" data-testid="register-section">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-0 h-[26rem] w-[26rem] rounded-full bg-[#0FA3E8]/10 blur-3xl"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="font-mono-brand text-sm font-semibold text-[#0FA3E8]">/ 04</p>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2B4B]">
            Register your interest
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Two minutes now saves you a seat, and tells us exactly what to cover so the session
            is worth every minute of your time.
          </p>
          <ul className="mt-8 space-y-5">
            {nextSteps.map((step, i) => (
              <motion.li
                key={step.text}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.15 + i * 0.1 }}
                className="flex items-center gap-4"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#0B2B4B] text-[#38BDF8]">
                  <step.icon className="h-5 w-5" />
                </span>
                <p className="text-sm font-medium text-slate-600">{step.text}</p>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-10 shadow-xl shadow-[#0B2B4B]/5">
            <AnimatePresence mode="wait">
              {successId ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="py-10 text-center"
                  data-testid="registration-success"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.15 }}
                    className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50"
                  >
                    <CheckCircle2 className="h-9 w-9 text-emerald-500" />
                  </motion.div>
                  <h3 className="font-display mt-6 text-2xl font-bold text-[#0B2B4B]">
                    You're on the list, {form.first_name}.
                  </h3>
                  <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-500">
                    Thanks for registering your interest. We'll be in touch with webinar details
                    as soon as they're locked in.
                  </p>
                  <p className="mt-5 inline-block rounded-full bg-slate-100 px-4 py-1.5 font-mono-brand text-xs text-slate-500">
                    Reference: {successId.slice(0, 8).toUpperCase()}
                  </p>
                  <div className="mt-8">
                    <button
                      onClick={() => {
                        setForm(emptyForm);
                        setSuccessId(null);
                      }}
                      className="text-sm font-semibold text-[#0284C7] hover:text-[#0B2B4B] transition-colors duration-200"
                      data-testid="register-another-btn"
                    >
                      Register another person
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={submit}
                  noValidate
                  className="space-y-6"
                  data-testid="eoi-registration-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="First name" required error={errors.first_name} testid="field-first-name">
                      <input
                        type="text"
                        value={form.first_name}
                        onChange={set("first_name")}
                        placeholder="Jane"
                        className={inputCls}
                        data-testid="input-first-name"
                      />
                    </Field>
                    <Field label="Last name" required error={errors.last_name} testid="field-last-name">
                      <input
                        type="text"
                        value={form.last_name}
                        onChange={set("last_name")}
                        placeholder="Citizen"
                        className={inputCls}
                        data-testid="input-last-name"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Email address" required error={errors.email} testid="field-email">
                      <input
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="jane@business.com.au"
                        className={inputCls}
                        data-testid="input-email"
                      />
                    </Field>
                    <Field label="Mobile number" error={errors.mobile} testid="field-mobile">
                      <input
                        type="tel"
                        value={form.mobile}
                        onChange={set("mobile")}
                        placeholder="04XX XXX XXX"
                        className={inputCls}
                        data-testid="input-mobile"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Business name" required error={errors.business_name} testid="field-business-name">
                      <input
                        type="text"
                        value={form.business_name}
                        onChange={set("business_name")}
                        placeholder="Your business"
                        className={inputCls}
                        data-testid="input-business-name"
                      />
                    </Field>
                    <Field label="What industry is your business in?" required error={errors.industry} testid="field-industry">
                      <div className="relative">
                        <select
                          value={form.industry}
                          onChange={set("industry")}
                          className={`${inputCls} appearance-none pr-10 ${form.industry ? "" : "text-slate-400"}`}
                          data-testid="select-industry"
                        >
                          <option value="" disabled>
                            Select your industry
                          </option>
                          {INDUSTRIES.map((ind) => (
                            <option key={ind} value={ind}>
                              {ind}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      </div>
                    </Field>
                  </div>

                  <Field label="How long have you been in business?" required error={errors.tenure} testid="field-tenure">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" role="radiogroup">
                      {TENURES.map((t) => (
                        <RadioCard
                          key={t}
                          name="tenure"
                          value={t}
                          label={t}
                          selected={form.tenure === t}
                          onSelect={set("tenure")}
                          testid={`radio-tenure-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        />
                      ))}
                    </div>
                  </Field>

                  <Field label="What is your biggest cash flow challenge right now? (select all that apply)" required error={errors.biggest_challenge} testid="field-challenge">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" role="group">
                      {CHALLENGES.map((c, i) => (
                        <CheckCard
                          key={c}
                          label={c}
                          selected={form.biggest_challenge.includes(c)}
                          onToggle={() => toggleChallenge(c)}
                          testid={`check-challenge-${i}`}
                        />
                      ))}
                    </div>
                  </Field>

                  <Field label="What would you most like to learn from this webinar? (optional)" error={errors.webinar_goal} testid="field-webinar-goal">
                    <textarea
                      rows={3}
                      value={form.webinar_goal}
                      onChange={set("webinar_goal")}
                      placeholder="Tell us in a sentence or two…"
                      className={`${inputCls} resize-none`}
                      data-testid="input-webinar-goal"
                    />
                  </Field>

                  <Field
                    label="Would you like to have a quick chat with EYB Consulting about your business or cash flow?"
                    required
                    error={errors.contact_consent}
                    testid="field-contact-consent"
                  >
                    <div className="grid grid-cols-2 gap-2.5" role="radiogroup">
                      {["Yes", "Not at this stage"].map((opt) => (
                        <RadioCard
                          key={opt}
                          name="contact_consent"
                          value={opt}
                          label={opt}
                          selected={form.contact_consent === opt}
                          onSelect={set("contact_consent")}
                          testid={`radio-consent-${opt === "Yes" ? "yes" : "no"}`}
                        />
                      ))}
                    </div>
                  </Field>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="group flex w-full items-center justify-center gap-3 rounded-2xl bg-[#0B2B4B] px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-[#0B2B4B]/20 transition-all duration-200 hover:bg-[#0FA3E8] hover:shadow-[#0FA3E8]/30 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none"
                    data-testid="register-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Registering…
                      </>
                    ) : (
                      "Register my interest"
                    )}
                  </button>
                  <p className="text-center text-xs text-slate-400">
                    We respect your inbox: only webinar details and what you ask for.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
